import { Box } from "@mui/material";
import { useCallback, useRef, useState, type ReactNode } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";

const MSME_CUBES_BACKGROUND =
  "url('https://www.transparenttextures.com/patterns/cubes.png')";

interface ParallaxTransform {
  x: number;
  y: number;
  rotateX: number;
  rotateY: number;
}

const IDLE_TRANSFORM: ParallaxTransform = { x: 0, y: 0, rotateX: 0, rotateY: 0 };

interface MsmeAuthParallaxBackgroundProps {
  children: ReactNode;
}

const MsmeAuthParallaxBackground: React.FC<MsmeAuthParallaxBackgroundProps> = ({
  children,
}) => {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const finePointer = useMediaQuery("(pointer: fine)");
  const parallaxEnabled = finePointer && !prefersReducedMotion;

  const frameRef = useRef<number | undefined>(undefined);
  const targetRef = useRef({ x: 0, y: 0 });
  const [transform, setTransform] = useState<ParallaxTransform>(IDLE_TRANSFORM);

  const scheduleTransform = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = requestAnimationFrame(() => {
      const { x, y } = targetRef.current;
      setTransform({
        x: x * 32,
        y: y * 32,
        rotateX: -y * 5,
        rotateY: x * 5,
      });
    });
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!parallaxEnabled) return;

      const rect = event.currentTarget.getBoundingClientRect();
      targetRef.current = {
        x: (event.clientX - rect.left) / rect.width - 0.5,
        y: (event.clientY - rect.top) / rect.height - 0.5,
      };
      scheduleTransform();
    },
    [parallaxEnabled, scheduleTransform]
  );

  const handleMouseLeave = useCallback(() => {
    targetRef.current = { x: 0, y: 0 };
    setTransform(IDLE_TRANSFORM);
  }, []);

  const sheenX = 50 + transform.rotateY * 2.5;
  const sheenY = 50 - transform.rotateX * 2.5;

  return (
    <Box
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: "relative",
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "inherit",
        alignItems: "inherit",
        justifyContent: "inherit",
        alignSelf: "stretch",
        width: "100%",
        minHeight: 0,
        backgroundColor: "#f9fafb",
        overflow: "hidden",
        perspective: parallaxEnabled ? "1100px" : "none",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-14%",
          backgroundColor: "#f9fafb",
          backgroundImage: MSME_CUBES_BACKGROUND,
          transform: parallaxEnabled
            ? `translate3d(${transform.x}px, ${transform.y}px, 0) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) scale(1.14)`
            : "none",
          transformOrigin: "center center",
          transformStyle: "preserve-3d",
          transition: parallaxEnabled ? "transform 0.4s ease-out" : "none",
          willChange: parallaxEnabled ? "transform" : "auto",
          zIndex: 0,
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-10%",
          backgroundImage: MSME_CUBES_BACKGROUND,
          opacity: 0.28,
          filter: "blur(1.5px)",
          transform: parallaxEnabled
            ? `translate3d(${transform.x * 0.45}px, ${transform.y * 0.45}px, -48px) scale(1.1)`
            : "none",
          transformStyle: "preserve-3d",
          transition: parallaxEnabled ? "transform 0.5s ease-out" : "none",
          zIndex: 0,
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
          background: parallaxEnabled
            ? `radial-gradient(circle at ${sheenX}% ${sheenY}%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.12) 32%, transparent 68%)`
            : "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 40%)",
          backdropFilter: "blur(0.5px)",
          WebkitBackdropFilter: "blur(0.5px)",
          transition: parallaxEnabled ? "background 0.4s ease-out" : "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "inherit",
          alignItems: "inherit",
          justifyContent: "inherit",
          width: "100%",
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MsmeAuthParallaxBackground;
